import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import OptionPicker from "@/components/app/OptionPicker";
import FormErrorBoundary from "@/components/app/FormErrorBoundary";
import { X, Search, MapPin, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/app/ImageUploader";
import MiniLocationMap from "@/components/app/MiniLocationMap";
import PrecisionNote from "@/components/app/PrecisionNote";

const TYPES = [
  ["churrasco", "🔥 Churrasco"], ["resenha", "🍻 Resenha"], ["festa", "🎉 Festa"],
  ["atletica", "🏆 Atlética/República"], ["luau", "🌙 Luau"], ["show", "🎸 Show"],
  ["feira", "🛍️ Feira"], ["outro", "📍 Outro"],
];

function maskCep(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

export default function CreateEventSheet({ onClose, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: "", description: "", type: "resenha",
    date: today, start_time: "20:00", end_time: "23:00",
    price: "", max_people: "", cover_url: null,
  });
  const [cep, setCep] = useState("");
  const [cepNumber, setCepNumber] = useState("");
  const reqId = useRef(0);
  const [addr, setAddr] = useState("");
  const [complement, setComplement] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onCepChange = (v) => {
    const masked = maskCep(v);
    setCep(masked);
    const digits = masked.replace(/\D/g, "");
    if (digits.length === 8) setTimeout(() => searchCep(digits), 50);
  };

  const applyResults = (data, id) => {
    if (id !== reqId.current) return;
    setResults(data);
    setPicked(data[0] || null);
    if (data[0]?.complement) setComplement((c) => c || data[0].complement);
    if (data[0]) console.info("[endereço] localizado", data[0].label, data[0].lat, data[0].lng, data[0].precision);
  };

  const searchCep = async (digits, num = cepNumber) => {
    if (digits.length !== 8) return;
    const id = ++reqId.current;
    setSearching(true);
    setResults([]);
    setPicked(null);
    try {
      const params = { cep: digits };
      if (num.trim()) params.number = num.trim();
      const { data } = await api.get("/geocode", { params });
      if (!data.length && id === reqId.current) toast.error("Não achamos a rua desse CEP no mapa. Tente pelo endereço.");
      applyResults(data, id);
    } catch (e) {
      if (id === reqId.current) toast.error(formatApiError(e));
    } finally { if (id === reqId.current) setSearching(false); }
  };

  const search = async (type) => {
    if (type === "cep") return searchCep(cep.replace(/\D/g, ""));
    if (addr.trim().length < 3) { toast.error("Digite o endereço (rua e número)."); return; }
    const id = ++reqId.current;
    setSearching(true);
    setResults([]);
    setPicked(null);
    try {
      const { data } = await api.get("/geocode", { params: { q: addr } });
      if (!data.length && id === reqId.current) toast.error("Nada encontrado. Confira o nome da rua e o número.");
      applyResults(data, id);
    } catch (e) {
      if (id === reqId.current) toast.error(formatApiError(e));
    } finally { if (id === reqId.current) setSearching(false); }
  };

  useEffect(() => {
    const digits = cep.replace(/\D/g, "");
    if (digits.length !== 8 || !cepNumber.trim()) return undefined;
    const t = setTimeout(() => searchCep(digits, cepNumber), 900);
    return () => clearTimeout(t);
  }, [cepNumber]); // eslint-disable-line

  useEffect(() => {
    const q = addr.trim();
    if (q.length < 6 || !/\d/.test(q)) return undefined;
    const t = setTimeout(() => search("addr"), 1200);
    return () => clearTimeout(t);
  }, [addr]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!picked) { toast.error("Busque e escolha o endereço do rolê antes."); return; }
    if (!form.name.trim()) { toast.error("Dê um nome pro rolê."); return; }
    if (!form.date || !form.start_time || !form.end_time) { toast.error("Preencha data e horários."); return; }
    setLoading(true);
    try {
      const fullAddr = complement ? `${picked.label} (${complement})` : picked.label;
      await api.post("/events", {
        name: form.name,
        description: form.description,
        type: form.type,
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        lat: picked.lat,
        lng: picked.lng,
        address: fullAddr,
        price: form.price || null,
        max_people: form.max_people ? Number(form.max_people) : null,
        cover_url: form.cover_url || null,
      });
      toast.success("Rolê publicado! 🎉");
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <Sheet open onOpenChange={(o)=>{ if (!o) onClose?.(); }}>
      <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto rounded-t-3xl bg-card border-t border-white/10" data-testid="create-event-sheet">
        <div className="relative -mt-2 mb-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-white/20" />
          <button type="button" onClick={onClose} className="absolute top-0 right-0 h-9 w-9 rounded-full bg-muted grid place-items-center press" data-testid="create-close"><X className="w-4 h-4" /></button>
        </div>

        <SheetHeader className="text-left">
          <SheetTitle className="text-2xl heading font-extrabold">Novo rolê</SheetTitle>
          <p className="text-xs text-muted-foreground">Até 30 dias de antecedência. Buscamos o endereço pra deixar o pin no lugar certo.</p>
        </SheetHeader>

        <FormErrorBoundary name="criar-role">
        <form onSubmit={submit} className="mt-4 space-y-5 pb-24 notranslate" translate="no">
          <ImageUploader value={form.cover_url} onChange={(v)=>setForm((f)=>({...f, cover_url: v}))} label="Capa do rolê (opcional)" testid="create-cover" />

          {/* Address finder */}
          <div className="rounded-xl border border-white/10 bg-muted/30 p-3 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold"><MapPin className="w-4 h-4 text-primary" /> Onde vai ser</div>
            <div className="space-y-1.5">
              <Label className="text-xs">CEP + número (recomendado)</Label>
              <div className="flex gap-2">
                <Input inputMode="numeric" placeholder="00000-000" value={cep} onChange={(e)=>onCepChange(e.target.value)} maxLength={9} data-testid="create-cep" />
                <Input inputMode="numeric" placeholder="Nº" value={cepNumber} onChange={(e)=>setCepNumber(e.target.value.replace(/\D/g, "").slice(0, 5))} className="w-24" data-testid="create-cep-number" />
                <Button type="button" onClick={()=>search("cep")} disabled={searching} className="press" data-testid="create-cep-search">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="relative text-center">
              <div className="h-px bg-white/10" />
              <span className="absolute left-1/2 -translate-x-1/2 -top-2 bg-card px-2 text-[10px] text-muted-foreground uppercase">ou</span>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Endereço (rua e número)</Label>
              <div className="flex gap-2">
                <Input placeholder="Ex: Rua 9 de Julho, 450" value={addr} onChange={(e)=>setAddr(e.target.value)} data-testid="create-address" />
                <Button type="button" onClick={()=>search("addr")} disabled={searching} className="press" data-testid="create-address-search">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {results.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground">Toque pra escolher o local certo:</div>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {results.map((r, i) => {
                    const active = picked && picked.lat === r.lat && picked.lng === r.lng;
                    return (
                      <button key={i} type="button" onClick={()=>setPicked(r)} className={`w-full text-left rounded-lg border p-2.5 press ${active ? "border-primary bg-primary/10" : "border-white/10 bg-black/30"}`} data-testid={`geo-result-${i}`}>
                        <div className="flex items-start gap-2">
                          {active ? <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> : <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />}
                          <div className="text-sm leading-snug">
                            <div className="font-medium">{r.label || r.full}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {picked && (
              <>
                <MiniLocationMap
                  lat={picked.lat}
                  lng={picked.lng}
                  onChange={({ lat, lng }) => setPicked((p) => ({ ...p, lat, lng, adjusted: true }))}
                />
                {picked.adjusted ? (
                  <p className="text-[11px] text-primary flex items-center gap-1" data-testid="pin-adjusted-note">
                    <Check className="w-3 h-3" /> Pin ajustado manualmente — vai cair exatamente aí.
                  </p>
                ) : <PrecisionNote precision={picked.precision} testid="create-geo-precision" />}
                <div className="space-y-1.5">
                  <Label className="text-xs">Complemento (opcional)</Label>
                  <Input value={complement} onChange={(e)=>setComplement(e.target.value)} placeholder="apto, bloco, ponto de referência" data-testid="create-complement" />
                </div>
              </>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Nome do rolê</Label>
            <Input required value={form.name} onChange={change("name")} maxLength={80} data-testid="create-name" placeholder="Ex: Churras no Zé" />
          </div>
          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <OptionPicker value={form.type} onChange={change("type")} options={TYPES.map(([k, l]) => ({ value: k, label: l }))} testid="create-type" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5 col-span-3">
              <Label>Data</Label>
              <Input type="date" required value={form.date} min={today} onChange={change("date")} data-testid="create-date" />
            </div>
            <div className="space-y-1.5 col-span-1">
              <Label>Início</Label>
              <Input type="time" required value={form.start_time} onChange={change("start_time")} data-testid="create-start" />
            </div>
            <div className="space-y-1.5 col-span-1">
              <Label>Término</Label>
              <Input type="time" required value={form.end_time} onChange={change("end_time")} data-testid="create-end" />
            </div>
            <div className="col-span-1 text-xs text-muted-foreground self-end pb-2.5">pós-00h = madrugada</div>
          </div>
          <div className="space-y-1.5">
            <Label>Descrição</Label>
            <Textarea value={form.description} onChange={change("description")} maxLength={1000} data-testid="create-desc" placeholder="Fala pra galera o que vai rolar..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Valor / Rateio</Label>
              <Input value={form.price} onChange={change("price")} data-testid="create-price" placeholder="opcional" />
            </div>
            <div className="space-y-1.5">
              <Label>Limite de pessoas</Label>
              <Input type="number" min="0" value={form.max_people} onChange={change("max_people")} data-testid="create-max" placeholder="opcional" />
            </div>
          </div>

          <div className="rounded-lg bg-muted/60 border border-border p-3 text-xs text-muted-foreground">
            Lembre-se: o Enkosta só divulga eventos. Você é responsável pelo rolê. Evento com bebida alcoólica é só para maiores de 18. Beba com moderação e não dirija.
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-12 press" data-testid="create-cancel">Cancelar</Button>
            <Button type="submit" disabled={loading} onClick={(e)=>{ if (!picked) { e.preventDefault(); toast.error("Busque e escolha o endereço do rolê antes."); }}} className="flex-1 h-12 press" data-testid="create-submit">
              {loading ? "Publicando..." : "Publicar rolê"}
            </Button>
          </div>
        </form>
        </FormErrorBoundary>
      </SheetContent>
    </Sheet>
  );
}
